import { api } from "@/lib/api";
import { downscaleToBlob } from "@/lib/img";

/**
 * Big files (yoga videos) are sliced in the browser: the hosting proxy caps how
 * much can be sent in a single request. Each chunk is a separate POST and the
 * server stitches them together on /complete.
 */
export async function uploadVideoChunked(file, purpose = "yoga", { onProgress } = {}) {
  const { data: session } = await api.post("/files/upload/init", {
    filename: file.name || "video.mp4",
    content_type: file.type || "video/mp4",
    size: file.size,
    purpose,
  });
  const chunkSize = session.chunk_size || 4 * 1024 * 1024;
  const total = Math.ceil(file.size / chunkSize);
  for (let index = 0; index < total; index += 1) {
    const blob = file.slice(index * chunkSize, (index + 1) * chunkSize);
    const form = new FormData();
    form.append("upload_id", session.upload_id);
    form.append("index", String(index));
    form.append("file", blob, `part-${index}`);
    await api.post("/files/upload/chunk", form, { headers: { "Content-Type": "multipart/form-data" } });
    if (onProgress) onProgress(Math.round(((index + 1) / total) * 95));
  }
  const { data } = await api.post("/files/upload/complete", { upload_id: session.upload_id });
  if (onProgress) onProgress(100);
  return data;
}

/**
 * Uploads a file to object storage through the backend and returns
 * { id, url, size, content_type }. Images are downscaled first so uploads stay quick.
 */
export async function uploadFile(file, purpose, { maxWidth = 1400, quality = 0.85, raw = false, onProgress } = {}) {
  let payload = file;
  let filename = file.name || "upload";
  if (!raw && file.type?.startsWith("image/") && file.type !== "image/gif") {
    payload = await downscaleToBlob(file, maxWidth, quality);
    filename = filename.replace(/\.[^.]+$/, "") + ".jpg";
  }
  const form = new FormData();
  form.append("file", payload, filename);
  form.append("purpose", purpose);
  const { data } = await api.post("/files/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  return data;
}
